import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Search,
  Shield,
  DollarSign,
  FileText,
  TrendingUp,
  UserCheck,
  UserX,
  Eye,
  EyeOff,
  ArrowLeft,
  LogOut,
  Settings,
  KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { value: 'finance_admin', label: 'Finance Admin', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'hr_admin', label: 'HR Admin', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'doc_admin', label: 'Doc Admin', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  { value: 'sales_admin', label: 'Sales Admin', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 'employee', label: 'Employee', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
];

const PRODUCTS = [
  { id: 'finance', name: 'MyFinance Orbit', icon: DollarSign, color: 'text-green-400' },
  { id: 'hr', name: 'MyHR Orbit', icon: Users, color: 'text-blue-400' },
  { id: 'docs', name: 'MyDoc Orbit', icon: FileText, color: 'text-purple-400' },
  { id: 'sales', name: 'MySales Orbit', icon: TrendingUp, color: 'text-orange-400' },
  { id: 'website_admin', name: 'Website Admin', icon: Settings, color: 'text-slate-400' },
];

// Module-specific role options
const SALES_ROLES = [
  { value: 'ceo', label: 'CEO', description: 'Full access to all sales data' },
  { value: 'cso', label: 'CSO', description: 'Company-wide sales oversight' },
  { value: 'director', label: 'Director', description: 'Team Locator, manage team, analytics' },
  { value: 'manager', label: 'Manager', description: 'Team management, assign leads' },
  { value: 'sales_rep', label: 'Sales Rep', description: 'Own leads and contacts only' },
];

const HR_ROLES = [
  { value: 'hr_director', label: 'HR Director', description: 'Full HR access, policy changes' },
  { value: 'hr_manager', label: 'HR Manager', description: 'Approve leaves, run payroll' },
  { value: 'hr_executive', label: 'HR Executive', description: 'Process requests, view team' },
  { value: 'employee', label: 'Employee', description: 'Self-service only (auto-assigned)' },
];

const FINANCE_ROLES = [
  { value: 'finance_director', label: 'Finance Director', description: 'Full access, unlimited approvals' },
  { value: 'finance_manager', label: 'Finance Manager', description: 'Approve expenses, manage vendors' },
  { value: 'accountant', label: 'Accountant', description: 'Create invoices, record expenses' },
  { value: 'viewer', label: 'Viewer', description: 'View reports only (read-only)' },
];

const DOC_ROLES = [
  { value: 'doc_admin', label: 'Doc Admin', description: 'Full access, system settings' },
  { value: 'doc_manager', label: 'Doc Manager', description: 'Manage all documents' },
  { value: 'department_user', label: 'Department User', description: 'Upload docs, answer queries for dept' },
  { value: 'chat_only', label: 'Chat Only', description: 'AI chat and view public docs only' },
];

const DEPARTMENTS = [
  { value: 'sales', label: 'Sales' },
  { value: 'hr', label: 'HR' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'it', label: 'IT' },
  { value: 'legal', label: 'Legal' },
];

export default function SuperAdminUserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    products: [],
    is_active: true,
    sales_role: '',
    hr_role: '',
    finance_role: '',
    doc_role: '',
    doc_department: ''
  });

  useEffect(() => {
    // Check authentication and role
    const userData = localStorage.getItem('userData');
    const token = localStorage.getItem('authToken');
    
    if (!userData || !token) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'super_admin') {
      toast.error('Access denied. Super Admin only.');
      navigate('/portal');
      return;
    }

    setCurrentUser(parsedUser);
    fetchUsers();
  }, [navigate]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Super Admin only.');
        navigate('/portal');
      } else {
        toast.error('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          role: formData.role,
          products: formData.products,
          is_active: formData.is_active,
          sales_role: formData.products.includes('sales') ? formData.sales_role : null,
          hr_role: formData.products.includes('hr') ? formData.hr_role : null,
          finance_role: formData.products.includes('finance') ? formData.finance_role : null,
          doc_role: formData.products.includes('docs') ? formData.doc_role : null,
          doc_department: formData.doc_role === 'department_user' ? formData.doc_department : null
        };
        await axios.put(`${API}/users/${editingUser.id}`, updateData, { headers: getAuthHeader() });
        toast.success('User updated successfully');
      } else {
        const createData = {
          ...formData,
          sales_role: formData.products.includes('sales') ? formData.sales_role : null,
          hr_role: formData.products.includes('hr') ? formData.hr_role : null,
          finance_role: formData.products.includes('finance') ? formData.finance_role : null,
          doc_role: formData.products.includes('docs') ? formData.doc_role : null,
          doc_department: formData.doc_role === 'department_user' ? formData.doc_department : null
        };
        await axios.post(`${API}/users`, createData, { headers: getAuthHeader() });
        toast.success('User created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeader() });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openResetPasswordDialog = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setShowNewPassword(false);
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResettingPassword(true);
    try {
      await axios.post(
        `${API}/users/${resetPasswordUser.id}/reset-password`,
        { new_password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success(`Password reset successfully for ${resetPasswordUser.email}`);
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      products: Array.isArray(user.products) ? [...user.products] : [],
      is_active: user.is_active !== false,
      sales_role: user.sales_role || '',
      hr_role: user.hr_role || '',
      finance_role: user.finance_role || '',
      doc_role: user.doc_role || '',
      doc_department: user.doc_department || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'employee',
      products: [],
      is_active: true,
      sales_role: '',
      hr_role: '',
      finance_role: '',
      doc_role: '',
      doc_department: ''
    });
    setShowPassword(false);
  };

  const toggleProduct = (productId) => {
    setFormData(prev => {
      const newProducts = prev.products.includes(productId)
        ? prev.products.filter(p => p !== productId)
        : [...prev.products, productId];
      
      // Clear module role if product is deselected
      const updates = { products: newProducts };
      if (!newProducts.includes('sales')) updates.sales_role = '';
      if (!newProducts.includes('hr')) updates.hr_role = '';
      if (!newProducts.includes('finance')) updates.finance_role = '';
      if (!newProducts.includes('docs')) {
        updates.doc_role = '';
        updates.doc_department = '';
      }
      
      return { ...prev, ...updates };
    });
  };

  // Filter users based on search and active tab
  const getFilteredUsers = () => {
    let filtered = users.filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    switch (activeTab) {
      case 'hr':
        return filtered.filter(u => u.products?.includes('hr') || u.role === 'hr_admin');
      case 'finance':
        return filtered.filter(u => u.products?.includes('finance') || u.role === 'finance_admin');
      case 'sales':
        return filtered.filter(u => u.products?.includes('sales') || u.role === 'sales_admin');
      case 'docs':
        return filtered.filter(u => u.products?.includes('docs') || u.role === 'doc_admin');
      case 'employees':
        return filtered.filter(u => u.role === 'employee' || u.role === 'sales_user');
      case 'admins':
        return filtered.filter(u => u.role === 'super_admin' || u.role?.includes('admin'));
      default:
        return filtered;
    }
  };

  const filteredUsers = getFilteredUsers();

  // Get counts for each tab
  const tabCounts = {
    all: users.length,
    hr: users.filter(u => u.products?.includes('hr') || u.role === 'hr_admin').length,
    finance: users.filter(u => u.products?.includes('finance') || u.role === 'finance_admin').length,
    sales: users.filter(u => u.products?.includes('sales') || u.role === 'sales_admin').length,
    docs: users.filter(u => u.products?.includes('docs') || u.role === 'doc_admin').length,
    employees: users.filter(u => u.role === 'employee' || u.role === 'sales_user').length,
    admins: users.filter(u => u.role === 'super_admin' || u.role?.includes('admin')).length,
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role);
    return roleConfig ? (
      <Badge className={cn('font-medium', roleConfig.color)}>
        {roleConfig.label}
      </Badge>
    ) : null;
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Back */}
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/portal')} className="text-slate-300 hover:text-white hover:bg-slate-700">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Portal
              </Button>
              <div className="h-6 w-px bg-slate-600" />
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png" 
                  alt="Aurous" 
                  className="h-8 w-auto"
                />
              </Link>
            </div>

            {/* User info & actions */}
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{currentUser.name}</p>
                <p className="text-xs text-slate-400">{currentUser.email}</p>
              </div>
              
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">User Management</h1>
                  <p className="text-slate-400">Create users and manage product access</p>
                </div>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-700 bg-slate-800/50">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{users.length}</p>
                  <p className="text-xs text-slate-400">Total Users</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-700 bg-slate-800/50">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{users.filter(u => u.is_active).length}</p>
                  <p className="text-xs text-slate-400">Active</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-700 bg-slate-800/50">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{users.filter(u => u.role === 'super_admin').length}</p>
                  <p className="text-xs text-slate-400">Admins</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-700 bg-slate-800/50">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-slate-500/20 flex items-center justify-center">
                  <UserX className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{users.filter(u => !u.is_active).length}</p>
                  <p className="text-xs text-slate-400">Inactive</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs and Users Table */}
          <Card className="border-slate-700 bg-slate-800/50">
            <CardHeader className="pb-0 border-b border-slate-700">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle className="text-white">User Management</CardTitle>
                  <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    />
                  </div>
                </div>
                
                {/* Module Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="bg-slate-700/50 p-1 h-auto flex-wrap">
                    <TabsTrigger 
                      value="all" 
                      className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-300 px-4 py-2"
                    >
                      All Users
                      <Badge className="ml-2 bg-slate-600 text-slate-200 text-xs">{tabCounts.all}</Badge>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="hr" 
                      className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-300 px-4 py-2"
                    >
                      <Users className="w-4 h-4 mr-1 text-blue-400" />
                      HR Module
                      <Badge className="ml-2 bg-blue-500/20 text-blue-300 text-xs">{tabCounts.hr}</Badge>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="finance" 
                      className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-slate-300 px-4 py-2"
                    >
                      <DollarSign className="w-4 h-4 mr-1 text-green-400" />
                      Finance Module
                      <Badge className="ml-2 bg-green-500/20 text-green-300 text-xs">{tabCounts.finance}</Badge>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="sales" 
                      className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300 px-4 py-2"
                    >
                      <TrendingUp className="w-4 h-4 mr-1 text-orange-400" />
                      Sales Module
                      <Badge className="ml-2 bg-orange-500/20 text-orange-300 text-xs">{tabCounts.sales}</Badge>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="docs" 
                      className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-300 px-4 py-2"
                    >
                      <FileText className="w-4 h-4 mr-1 text-purple-400" />
                      Docs Module
                      <Badge className="ml-2 bg-purple-500/20 text-purple-300 text-xs">{tabCounts.docs}</Badge>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="employees" 
                      className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white text-slate-300 px-4 py-2"
                    >
                      <UserCheck className="w-4 h-4 mr-1 text-cyan-400" />
                      Employees
                      <Badge className="ml-2 bg-cyan-500/20 text-cyan-300 text-xs">{tabCounts.employees}</Badge>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="admins" 
                      className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-300 px-4 py-2"
                    >
                      <Shield className="w-4 h-4 mr-1 text-red-400" />
                      Admins
                      <Badge className="ml-2 bg-red-500/20 text-red-300 text-xs">{tabCounts.admins}</Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">No users found in this category</p>
                  <p className="text-sm text-slate-500 mt-1">Try a different tab or search term</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-transparent">
                        <TableHead className="text-slate-300">User</TableHead>
                        <TableHead className="text-slate-300">Role</TableHead>
                        <TableHead className="text-slate-300">Products</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                        <TableHead className="text-right text-slate-300">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id} className="border-slate-700 hover:bg-slate-700/50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-white">{user.name}</p>
                              <p className="text-sm text-slate-400">{user.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getRoleBadge(user.role)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {user.role === 'super_admin' ? (
                                <Badge className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs border-0">All Products</Badge>
                              ) : user.products?.length > 0 ? (
                                user.products.map(p => {
                                  const product = PRODUCTS.find(prod => prod.id === p);
                                  const colors = {
                                    finance: 'bg-green-500/20 text-green-400 border-green-500/30',
                                    hr: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                                    docs: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                                    sales: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
                                    website_admin: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                                  };
                                  return (
                                    <Badge key={p} className={`text-xs capitalize ${colors[p] || 'bg-slate-500/20 text-slate-400'}`}>
                                      {product?.name?.replace(' Orbit', '') || p}
                                    </Badge>
                                  );
                                })
                              ) : (
                                <span className="text-slate-500 text-sm">None</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {user.is_active ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>
                            ) : (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openResetPasswordDialog(user)} className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10" title="Reset Password">
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} className="text-slate-400 hover:text-white hover:bg-slate-700" title="Edit User">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => handleDelete(user.id)} title="Delete User">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-slate-500">
        <p>© 2025 Aurous Consultancy. All rights reserved.</p>
      </footer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingUser ? 'Update user details and permissions' : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@company.com"
                disabled={editingUser}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 disabled:opacity-50"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value} className="text-white hover:bg-slate-700">{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.role !== 'super_admin' && (
              <div className="space-y-2">
                <Label className="text-slate-300">Product Access</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PRODUCTS.map(product => {
                    const Icon = product.icon;
                    const isSelected = formData.products.includes(product.id);
                    return (
                      <div
                        key={product.id}
                        onClick={() => toggleProduct(product.id)}
                        className={cn(
                          'flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all',
                          isSelected ? 'border-blue-500 bg-blue-500/20' : 'border-slate-600 hover:border-slate-500 bg-slate-700/50'
                        )}
                      >
                        <div 
                          className={cn(
                            'w-4 h-4 rounded border-2 flex items-center justify-center',
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-500'
                          )}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <Icon className={cn('w-4 h-4', product.color)} />
                        <span className="text-sm font-medium text-slate-200">{product.name.replace('My', '').replace(' Orbit', '')}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Module-specific Role Selectors */}
            {formData.products.includes('sales') && (
              <div className="space-y-2 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <Label className="text-orange-400 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Sales Role
                </Label>
                <Select value={formData.sales_role} onValueChange={(v) => setFormData({ ...formData, sales_role: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select sales role..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {SALES_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value} className="text-white hover:bg-slate-700">
                        <div>
                          <span className="font-medium">{role.label}</span>
                          <span className="text-xs text-slate-400 ml-2">- {role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.products.includes('hr') && (
              <div className="space-y-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <Label className="text-blue-400 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  HR Role
                </Label>
                <Select value={formData.hr_role} onValueChange={(v) => setFormData({ ...formData, hr_role: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select HR role..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {HR_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value} className="text-white hover:bg-slate-700">
                        <div>
                          <span className="font-medium">{role.label}</span>
                          <span className="text-xs text-slate-400 ml-2">- {role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.products.includes('finance') && (
              <div className="space-y-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                <Label className="text-green-400 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Finance Role
                </Label>
                <Select value={formData.finance_role} onValueChange={(v) => setFormData({ ...formData, finance_role: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select finance role..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {FINANCE_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value} className="text-white hover:bg-slate-700">
                        <div>
                          <span className="font-medium">{role.label}</span>
                          <span className="text-xs text-slate-400 ml-2">- {role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.products.includes('docs') && (
              <div className="space-y-2 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <Label className="text-purple-400 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Doc Role
                </Label>
                <Select value={formData.doc_role} onValueChange={(v) => setFormData({ ...formData, doc_role: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select doc role..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {DOC_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value} className="text-white hover:bg-slate-700">
                        <div>
                          <span className="font-medium">{role.label}</span>
                          <span className="text-xs text-slate-400 ml-2">- {role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {formData.doc_role === 'department_user' && (
                  <div className="mt-2">
                    <Label className="text-slate-300 text-xs">Department</Label>
                    <Select value={formData.doc_department} onValueChange={(v) => setFormData({ ...formData, doc_department: v })}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                        <SelectValue placeholder="Select department..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {DEPARTMENTS.map(dept => (
                          <SelectItem key={dept.value} value={dept.value} className="text-white hover:bg-slate-700">
                            {dept.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active === true}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: Boolean(checked) })}
                className="border-slate-500 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
              />
              <Label htmlFor="is_active" className="text-slate-300">Account is active</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-yellow-400" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Set a new password for <span className="text-white font-medium">{resetPasswordUser?.email}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-slate-300">New Password *</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">Password must be at least 6 characters long</p>
            </div>

            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-sm text-yellow-300">
                <strong>Warning:</strong> The user will need to use this new password on their next login.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setResetPasswordDialogOpen(false)} 
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                disabled={resettingPassword}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleResetPassword} 
                className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                disabled={resettingPassword || !newPassword || newPassword.length < 6}
                data-testid="reset-password-submit"
              >
                {resettingPassword ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
